
import * as THREE from 'three';
import ClipperLib from '../../../3dapps/libs/clipper_unminified';
import { ClosedSplineCurve } from './ClosedSplineCurve';
import { Line2 } from './Line2';
import { ImageDataReader } from './ImageDataReader';

function nextIndex(i, len) {
    let j = i + 1;
    if (j >= len) j = 0;
    return j;
}

function vector2ToFPoint(p) {
    return new ClipperLib.FPoint(p.x, p.y);
}
function fPointToVector2(p) {
    return new THREE.Vector2(p.X, p.Y);
}

// 圆的坐标缓存
let CIRCLE_POINTS_CACHE = [];
// 圆的切线缓存
let CIRCLE_TANGENTS_CACHE = [];
const PI_2_1 = Math.PI / 2;
const CIRCLE_DIVISION_2 = 1024;
// console.time("cacl circle cache");
for (let i = 0; i < CIRCLE_DIVISION_2; i++) {
    let rad = i / CIRCLE_DIVISION_2 * Math.PI * 2;
    CIRCLE_POINTS_CACHE.push(new THREE.Vector2(Math.cos(rad), Math.sin(rad)));
    let rad2 = rad + PI_2_1;
    CIRCLE_TANGENTS_CACHE.push(new THREE.Vector2(Math.cos(rad2), Math.sin(rad2)));
}
// console.timeEnd("cacl circle cache");

/*

方法说明：
1. 花瓶的正式更新方法：makeCircleWithRelief
2. 拖动曲线变化：makeCircleForDisplay
3. 修改浮雕的上下位置：updateReliefForDisplay
4. 修改浮雕的大小：updateReliefForDisplay
5. 浮雕花瓶的底部位置：updateBottomForDisplay
 */

export class VaseMaker {
    // 瓶身曲线的细分次数
    CURVE_DIVISION = 300;
    // 多边形模式下，连接边是曲线还是直线
    edgeIsLine = false;
    // 正多边形模式下，多边形的边数
    repeatTimes = 10;
    // 正多边形模式下，且是曲线的情况下，每段曲线的细分次数
    EACH_DIVISION = 20;
    // 圆形模式下，圆的细分次数
    CIRCLE_DIVISION = 300;
    CURVE_DIVISION_2 = 1024;
    CIRCLE_DIVISION_2 = CIRCLE_DIVISION_2;
    // 旋转弧度
    rotation = 0;
    // 瓶子的厚度
    thickness = 1.0;
    // 缩放
    scale = 0.45;
    // 封底位置[0, 1]
    bottomPosition = 0.9;
    // 旋转类型
    // line -> 直线
    // > -> 破折号形状
    // sin -> sin函数形状，[0, PI]
    // sin2 -> sin函数形状，[0, 2 * PI]
    rotationType = "line";

    // 当前模型的类型
    // circle: 无浮雕圆
    // circleWithRelief: 浮雕圆
    // regularPolygon: 正多边形
    // polygon: 多边形
    lastType = "";
    // 曲线分割段数
    lastCurvePointsLength = 0;
    // 圆分割段数
    lastCirclePointsLength = 0;

    minPointZ = -100;

    constructor() { }

    // 构造一个mesh
    createMesh(scene) {
        if (this.mesh) { scene.remove(this.mesh); this.mesh.geometry.dispose(); this.mesh = null; }
        if (this.meshForDisplay) { scene.remove(this.meshForDisplay); this.meshForDisplay.geometry.dispose(); this.meshForDisplay = null; }

        this.geom = new THREE.BufferGeometry();
        let material = new THREE.MeshPhongMaterial({ flatShading: true });
        this.mesh = new THREE.Mesh(this.geom, material);
        this.mesh.rotation.x = -Math.PI / 2;
        this.mesh.castShadow = true;
        scene.add(this.mesh);

        this.meshForDisplay = new THREE.Mesh(new THREE.BufferGeometry(), new THREE.MeshPhongMaterial({
            side: THREE.DoubleSide,
            wireframe: true,
            visible: false,
            vertexColors: THREE.VertexColors
        }));
        this.meshForDisplay.rotation.copy(this.mesh.rotation);
        scene.add(this.meshForDisplay);
        this.geomForDisplayCreated = false;
    }

    // 工具函数：构造边曲线，由一般圆、正多边形和多边形三者调用
    getSolidPoints(curve) {
        let curvePoints = curve.getSpacedPoints(this.CURVE_DIVISION);
        let scale = this.scale;
        curvePoints.forEach(p => {
            p.x -= 60;
            p.multiplyScalar(scale);
        });
        // 最后一点，到0点，也就是y轴竖直的位置
        let bottomPoint;
        if (this.bottomPosition === 1.0) {
            bottomPoint = curvePoints[curvePoints.length - 1];
        } else {
            bottomPoint = curve.getPointAt(this.bottomPosition);
            bottomPoint.x -= 60;
            bottomPoint.multiplyScalar(scale);
        }
        let lastCenterPoint = new THREE.Vector2(0, bottomPoint.y);
        let bottomSegment = [bottomPoint, lastCenterPoint];
        // curvePoints.push(lastCenterPoint);
        let curvePath = [curvePoints.map(vector2ToFPoint), bottomSegment.map(vector2ToFPoint)];
        let solution = new ClipperLib.Paths();
        // let co = new ClipperLib.ClipperOffset(2, 0.25);
        let co = new ClipperLib.ClipperOffset(2, 0.01);
        co.AddPaths(curvePath, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etOpenRound);
        co.Execute(solution, this.thickness);
        // if (solution.length > 1) throw "self intersect";

        let points = solution[0].map(fPointToVector2);
        // 末尾部分，切除多余的部分
        const lastDist = 0.1; // 判断的距离为1。由于控制点距离y轴的距离为4，所以这个余量是充足的。
        let lin = new Line2();
        let inters = [];
        for (let i = 0, j = 0; i < points.length; i++) {
            j = i >= points.length - 1 ? 0 : i + 1;
            lin.set(points[i], points[j]);
            let xRange = lin.end.x - lin.start.x;
            if (xRange === 0) { continue; }
            let t = (-lastDist - lin.start.x) / xRange;
            if (t >= 0 && t <= 1) {
                let inter = lin.at(t);
                inters.push({ inter, index: i, t, a: lin.start.clone(), b: lin.end.clone(), dist: inter.distanceTo(lastCenterPoint) });
            }
        }
        inters.sort((a, b) => a.dist - b.dist);
        inters.splice(2, inters.length - 2);
        inters.sort((a, b) => a.index - b.index);
        let startIndex = inters[0].index;
        let endIndex = inters[1].index;
        // 剪开线条，重排
        let points2 = [];
        for (let i = endIndex; i < points.length; i++) { points2.push(points[i]); }
        for (let i = 0; i <= startIndex + 1; i++) { points2.push(points[i]); }

        // points.splice(startIndex + 2, endIndex - startIndex - 2);
        // console.log("points length", endIndex - startIndex - 2, points.length, inters, startIndex, endIndex);
        return points2;
    }

    // 工具函数：构造边曲线
    // 不变形，由浮雕圆调用
    getSolidPoints3(curve) {
        let curvePoints = curve.getSpacedPoints(this.CURVE_DIVISION_2);
        // 最后一点，到0点，也就是y轴竖直的位置
        let bottomPoint;
        if (this.bottomPosition === 1.0) {
            bottomPoint = curvePoints[curvePoints.length - 1];
        } else {
            bottomPoint = curve.getPointAt(this.bottomPosition);
        }
        let lastCenterPoint = new THREE.Vector2(0, bottomPoint.y);
        let bottomSegment = [bottomPoint, lastCenterPoint];
        // curvePoints.push(lastCenterPoint);
        let curvePath = [curvePoints.map(vector2ToFPoint), bottomSegment.map(vector2ToFPoint)];
        let solution = new ClipperLib.Paths();
        // let co = new ClipperLib.ClipperOffset(2, 0.25);
        let co = new ClipperLib.ClipperOffset(2, 0.01);
        co.AddPaths(curvePath, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etOpenRound);
        co.Execute(solution, this.thickness);
        // if (solution.length > 1) throw "self intersect";

        let points = solution[0].map(fPointToVector2);
        // 末尾部分，切除多余的部分
        const lastDist = 0.1; // 判断的距离为1。由于控制点距离y轴的距离为4，所以这个余量是充足的。
        let lin = new Line2();
        let inters = [];
        for (let i = 0, j = 0; i < points.length; i++) {
            j = i >= points.length - 1 ? 0 : i + 1;
            lin.set(points[i], points[j]);
            let xRange = lin.end.x - lin.start.x;
            if (xRange === 0) { continue; }
            let t = (-lastDist - lin.start.x) / xRange;
            if (t >= 0 && t <= 1) {
                let inter = lin.at(t);
                inters.push({ inter, index: i, t, a: lin.start.clone(), b: lin.end.clone(), dist: inter.distanceTo(lastCenterPoint) });
            }
        }
        inters.sort((a, b) => a.dist - b.dist); // 按距离排序
        inters.splice(2, inters.length - 2); // 取最小的两个点
        // console.log("inters[0]", inters[0].index, inters[1].index, inters[0].inter.y, inters[1].inter.y);
        inters.sort((a, b) => a.index - b.index); // 按照下标排序
        // console.log("inters[0]", inters[0].index, inters[1].index, inters[0].inter.y, inters[1].inter.y);
        let startIndex = inters[0].index;
        let endIndex = inters[1].index;
        // 剪开线条，重排
        let points2 = [];
        for (let i = endIndex; i < points.length; i++) { points2.push(points[i]); }
        for (let i = 0; i <= startIndex + 1; i++) { points2.push(points[i]); }
        points2.reverse(); // 使其从下往上。这样一来，就总是先抵达上面的点
        return points2;
    }

    // 浮雕圆的瓶身曲线变化了
    makeCircleForDisplay(controlPoints, {
        stamp,
        curvePosition,
        circlePosition,
        stampWidthHalf,
        stampHeightHalf,
        keepSize,
        repeatTimes,
    }) {
        // console.log("xedfwioehfwoehfowe");
        let CURVE_DIVISION = 60;
        let NECK_DIVISION = 60;
        let pointsLen = CURVE_DIVISION + 1;
        let reuseGeometry = false;
        let vertices;
        let indices;
        let colors;
        // 由于展示用的不会被替换到，所以使用单独的频道来判断是否复用
        if (this.geomForDisplayCreated) {
            reuseGeometry = true;
            vertices = this.meshForDisplay.geometry.attributes.position.array;
            colors = this.meshForDisplay.geometry.attributes.color.array;
        } else {
            this.geomForDisplayCreated = true;
            // 瓶身的网状点 + 瓶底圆圈
            vertices = new Float32Array((pointsLen * NECK_DIVISION + NECK_DIVISION + 1) * 3);
            // 网状三角 + 瓶底圆圈三角
            // (pointsLen - 1) * NECK_DIVISION * 2 + NECK_DIVISION
            // -> CURVE_DIVISION * NECK_DIVISION * 2 + NECK_DIVISION
            indices = new Uint32Array((CURVE_DIVISION * NECK_DIVISION * 2 + NECK_DIVISION) * 3);
            // 顶点颜色，与点的数量一样
            colors = new Float32Array(vertices.length);
            // 瓶身的网格
            let ii = 0;
            for (let i = 1, lastCircleBegin = 0; i < pointsLen; i++) {
                let thisCircleBegin = i * NECK_DIVISION;
                for (let j = 0; j < NECK_DIVISION; j++) {
                    let k = nextIndex(j, NECK_DIVISION);
                    let i1 = lastCircleBegin + j;
                    let i2 = thisCircleBegin + j;
                    let i3 = lastCircleBegin + k;
                    let i4 = thisCircleBegin + k;
                    indices[ii] = i1; indices[ii + 1] = i4; indices[ii + 2] = i3;
                    indices[ii + 3] = i1; indices[ii + 4] = i2; indices[ii + 5] = i4;
                    ii += 6;
                }
                lastCircleBegin = thisCircleBegin;
            }
            // 瓶底圆圈
            let circleBegin = pointsLen * NECK_DIVISION;
            let circleCenterIndex = pointsLen * NECK_DIVISION + NECK_DIVISION;
            let circleEnd = circleCenterIndex - 1;
            for (let i = 0, lastIndex = circleEnd, currIndex = 0; i < NECK_DIVISION; i++) {
                currIndex = i + circleBegin;
                indices[ii] = lastIndex; indices[ii + 1] = currIndex; indices[ii + 2] = circleCenterIndex;
                ii += 3;
                lastIndex = currIndex;
            }
        }

        let scale = this.scale;
        controlPoints.forEach(p => {
            p.x = -p.x;
            p.x += 60;
            p.y = -p.y;
            p.multiplyScalar(scale);
        });

        let curve = new THREE.SplineCurve(controlPoints);
        // 曲线的总长度
        const curveLen = curve.getLength();
        // 根据图片的宽高比，计算实际的高度
        let curvePositionPoint = curve.getPointAt(curvePosition);

        let stampRealWidthHalf;
        if (repeatTimes) {
            stampWidthHalf = 1.0 / repeatTimes / 2;
            keepSize = false;
            stampRealWidthHalf = stampWidthHalf * 2 * Math.PI * curvePositionPoint.x;
            let stampRealHeightHalf = stampRealWidthHalf * (stamp.height - 1) / (stamp.width - 1);
            stampHeightHalf = stampRealHeightHalf / curveLen; // 计算垂直方向上的跨度，一半
        } if (stampHeightHalf) {
            let stampRealHeightHalf = stampHeightHalf * curveLen; // 计算真实的高度的一半
            stampRealWidthHalf = stampRealHeightHalf * (stamp.width - 1) / (stamp.height - 1); // 根据图片的长宽度计算实际的宽度的一半
            stampWidthHalf = stampRealWidthHalf / (2 * Math.PI * curvePositionPoint.x);
        } else {
            stampRealWidthHalf = stampWidthHalf * 2 * Math.PI * curvePositionPoint.x;
            let stampRealHeightHalf = stampRealWidthHalf * (stamp.height - 1) / (stamp.width - 1);
            stampHeightHalf = stampRealHeightHalf / curveLen;
        }

        let point = new THREE.Vector2();
        let p = new THREE.Vector3();

        let arcLen2 = circlePosition; // 中心点的相对位置
        let ii = 0;
        for (let i = 0, ty = 0, tx = 0, inRangeY = 0; i <= CURVE_DIVISION; i++) {
            let u = i / CURVE_DIVISION;
            curve.getPointAt(u, point);
            let s = point.x;
            let z = point.y;
            ty = (u - curvePosition) / stampHeightHalf / 2 + 0.5;
            inRangeY = ty >= 0 && ty <= 1.0;
            for (let j = 0; j < NECK_DIVISION; j++) {
                let v = j / NECK_DIVISION;
                let rad = Math.PI * 2 * v;
                let x = s * Math.cos(rad);
                let y = s * Math.sin(rad);

                vertices[ii] = x;
                vertices[ii + 1] = y;
                vertices[ii + 2] = z;

                if (inRangeY) {
                    let arcLen1 = v; // 当前位置的相对位置
                    if (repeatTimes) { tx = 0.5 }
                    else if (keepSize) { tx = (arcLen1 - arcLen2) * 2 * Math.PI * s / stampRealWidthHalf / 2 + 0.5; }
                    else { tx = (arcLen1 - arcLen2) / stampWidthHalf / 2 + 0.5; }

                    if (tx >= 0 && tx <= 1.0) {
                        colors[ii] = 0;
                        colors[ii + 1] = 0;
                        colors[ii + 2] = 1.0;
                        ii += 3;
                        continue;
                    }
                }
                colors[ii] = 1.0;
                colors[ii + 1] = 1.0;
                colors[ii + 2] = 1.0;
                ii += 3;
            }
        }

        // 最后一圈圆
        let circlePoint = curve.getPointAt(this.bottomPosition);
        for (let i = 0, z = circlePoint.y, circleRadius = circlePoint.x; i < NECK_DIVISION; i++) {
            let v = i / NECK_DIVISION;
            let rad = Math.PI * 2 * v;
            let x = circleRadius * Math.cos(rad);
            let y = circleRadius * Math.sin(rad);
            vertices[ii] = x;
            vertices[ii + 1] = y;
            vertices[ii + 2] = z;
            colors[ii] = 1.0;
            colors[ii + 1] = 0;
            colors[ii + 2] = 0;
            ii += 3;
        }
        vertices[ii] = 0;
        vertices[ii + 1] = 0;
        vertices[ii + 2] = circlePoint.y;
        colors[ii] = 1.0;
        colors[ii + 1] = 0;
        colors[ii + 2] = 0;

        if (reuseGeometry) {
            this.meshForDisplay.geometry.attributes.position.needsUpdate = true;
            this.meshForDisplay.geometry.attributes.color.needsUpdate = true;
            this.meshForDisplay.geometry.computeVertexNormals();
        } else {
            let geom = new THREE.BufferGeometry();
            geom.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
            geom.setAttribute("color", new THREE.BufferAttribute(colors, 3));
            geom.setIndex(new THREE.BufferAttribute(indices, 1));
            geom.computeVertexNormals();
            this.meshForDisplay.geometry.dispose();
            this.meshForDisplay.geometry = geom;
        }
        // console.log("this.meshForDisplay",this.meshForDisplay);
        this.meshForDisplay.material.visible = true;
        this.mesh.material.visible = false;
    }

    // 导出blob：只针对带浮雕图片
    exportBlob() {
        // 计算数组长度
        let n_bytes = 4; // NECK_DIVISION
        n_bytes += 4; // repeatTimes
        n_bytes += 4; // keepSize
        n_bytes += 8; // circlePosition
        n_bytes += 8; // stampWidthHalf
        n_bytes += 8; // stampRealWidthHalf
        n_bytes += 8; // reliefThickness
        n_bytes += 4; // points.length
        n_bytes += this.reliefData.points.length * 8 * 2; // points;
        n_bytes += 4; // topPointIndex
        n_bytes += 4; // bottomPointIndex
        n_bytes += this.reliefData.tyList.length * 8; // tyList
        n_bytes += 4; // depthData length
        n_bytes += this.reliefData.depthData.length * 4; // depthData

        // 创建数组和输出器
        let buffer = new ArrayBuffer(n_bytes);
        let writer = new DataView(buffer);
        let index = 0;

        writer.setInt32(index, this.reliefData.NECK_DIVISION, true); index += 4;
        writer.setInt32(index, this.reliefData.repeatTimes, true); index += 4;
        writer.setInt32(index, this.reliefData.keepSize ? 1 : 0, true); index += 4;
        writer.setFloat64(index, this.reliefData.circlePosition, true); index += 8;
        writer.setFloat64(index, this.reliefData.stampWidthHalf, true); index += 8;
        writer.setFloat64(index, this.reliefData.stampRealWidthHalf, true); index += 8;
        writer.setFloat64(index, this.reliefData.reliefThickness, true); index += 8;

        // 写入点列
        // console.log("points.length", this.reliefData.points);
        writer.setInt32(index, this.reliefData.points.length, true); index += 4;
        for (let p of this.reliefData.points) {
            writer.setFloat64(index, p.x, true); index += 8;
            writer.setFloat64(index, p.y, true); index += 8;
        }
        writer.setInt32(index, this.reliefData.topPointIndex, true); index += 4;
        writer.setInt32(index, this.reliefData.bottomPointIndex, true); index += 4;

        // tyList
        for (let ty of this.reliefData.tyList) {
            writer.setFloat64(index, ty, true); index += 8;
        }

        // depthData
        // console.log("depthData", this.reliefData.depthData);
        writer.setInt32(index, this.reliefData.depthData.length, true); index += 4;
        for (let depth of this.reliefData.depthData) {
            writer.setFloat32(index, depth, true); index += 4;
        }

        return new Blob([buffer], { type: "application/octet-binary" });
    }


    // 带厚度，带浮雕，均匀布局
    // stampWidthRatio: 浮雕水平方向所占的比例
    makeCircleWithRelief(controlPoints, {
        stamp,
        curvePosition,
        circlePosition,
        stampWidthHalf,
        stampHeightHalf,
        keepSize,
        repeatTimes,
        reliefThickness = 1.0
    }) {
        let scale = this.scale;
        controlPoints.forEach(p => {
            p.x = -p.x;
            p.x += 60;
            p.y = -p.y;
            p.multiplyScalar(scale);
        });
        let curve = new THREE.SplineCurve(controlPoints);
        // 这里的curvePoints已经是重新排列过的了，它的起始点是靠近中心点的地方
        let points = this.getSolidPoints3(curve);

        // console.time("makeCircleWithRelief3");
        let NECK_DIVISION = this.CIRCLE_DIVISION_2;
        let reuseGeometry = false;
        let vertices;
        let indices;

        const TYPE = "circleWithRelief";
        if (this.lastType === TYPE && points.length === this.lastCurvePointsLength && NECK_DIVISION === this.lastCirclePointsLength) {
            reuseGeometry = true;
            vertices = this.mesh.geometry.attributes.position.array;
        } else {
            this.lastType === TYPE;
            this.lastCurvePointsLength = points.length;
            this.lastCirclePointsLength = NECK_DIVISION;
            vertices = new Float32Array(points.length * NECK_DIVISION * 3); // 点的数量
            indices = new Uint32Array(points.length * NECK_DIVISION * 2 * 3); // 三角面的数量：两个方向上都是闭合的
            for (let i = 0, ii = 0, lastCircleBegin = points.length * NECK_DIVISION - NECK_DIVISION; i < points.length; i++) {
                let thisCircleBegin = i * NECK_DIVISION;
                for (let j = 0; j < NECK_DIVISION; j++) {
                    let k = nextIndex(j, NECK_DIVISION);
                    let i1 = lastCircleBegin + j;
                    let i2 = thisCircleBegin + j;
                    let i3 = lastCircleBegin + k;
                    let i4 = thisCircleBegin + k;
                    indices[ii] = i1; indices[ii + 1] = i4; indices[ii + 2] = i3;
                    indices[ii + 3] = i1; indices[ii + 4] = i2; indices[ii + 5] = i4;
                    ii += 6;
                }
                lastCircleBegin = thisCircleBegin;
            }
        }

        let firstPoint = controlPoints[0]; // 第一个点，瓶口顶点
        let lastPoint = controlPoints[controlPoints.length - 1]; // 最后一个点，底部
        // 搜索内部曲线的第一个点
        let minDist = Infinity;
        let minIndex = -1;
        for (let i = 0; i < points.length; i++) {
            let dist = points[i].distanceTo(firstPoint);
            if (dist <= minDist) { // 相等的话，取最后面的，如果是逆时针排列的，这个就是合适的方向
                minIndex = i;
                minDist = dist;
            }
        }
        let topPointIndex = minIndex;
        // 从第一个点往后，计算内部曲线的最后一个点
        // 这样可以避开内部的点
        minDist = Infinity;
        minIndex = -1;
        for (let i = topPointIndex; i < points.length; i++) {
            let dist = points[i].distanceTo(lastPoint);
            if (dist <= minDist) { // 相等的话，取最后面的，如果是逆时针排列的，这个就是合适的方向
                minIndex = i;
                minDist = dist;
            }
        }
        let bottomPointIndex = minIndex;
        // console.log("topPointIndex", topPointIndex, bottomPointIndex);
        let segmentLengthList = []; // 从头到尾每个线段的长度
        let totalLength = 0; // 外侧折线总长度
        for (let i = topPointIndex + 1; i <= bottomPointIndex; i++) {
            let len = points[i - 1].distanceTo(points[i]);
            segmentLengthList.push(len);
            totalLength += len;
        }

        // 计算浮雕的中心点的位置：确定一个下标
        // curvePosition = 1.0 - curvePosition;
        let curvePositionLen = totalLength * curvePosition; // 相对位置转绝对位置
        let curvePositionIndex = 0;
        let curvePositionLenAdjusted = 0; // 矫正过的浮雕位置长度
        for (let i = 0, currLen = 0, nextLen = 0; i < segmentLengthList.length; i++) {
            nextLen += segmentLengthList[i];
            if (nextLen >= curvePositionLen) {
                // 就在这一段，接下来确定是归上面的点还是下面的点
                if (nextLen - curvePositionLen <= curvePositionLen - currLen) {
                    curvePositionIndex = topPointIndex + i + 1;
                    curvePositionLenAdjusted = nextLen;
                } else {
                    curvePositionIndex = topPointIndex + i;
                    curvePositionLenAdjusted = currLen;
                }
                break;
            }
            currLen = nextLen;
        }

        let curvePositionPoint = points[curvePositionIndex];

        let stampRealWidthHalf;
        let stampRealHeightHalf;
        if (repeatTimes) {
            stampWidthHalf = 1.0 / repeatTimes / 2;
            keepSize = false;
            stampRealWidthHalf = stampWidthHalf * 2 * Math.PI * curvePositionPoint.x;
            stampRealHeightHalf = stampRealWidthHalf * (stamp.height - 1) / (stamp.width - 1);
        } if (stampHeightHalf) {
            stampRealHeightHalf = stampHeightHalf * totalLength; // 计算真实的高度的一半
            stampRealWidthHalf = stampRealHeightHalf * (stamp.width - 1) / (stamp.height - 1); // 根据图片的长宽度计算实际的宽度的一半
            stampWidthHalf = stampRealWidthHalf / (2 * Math.PI * curvePositionPoint.x);
        } else {
            stampRealWidthHalf = stampWidthHalf * 2 * Math.PI * curvePositionPoint.x;
            stampRealHeightHalf = stampRealWidthHalf * (stamp.height - 1) / (stamp.width - 1);
        }

        // 计算ty
        let tyList = [];
        for (let i = 0, len = 0; i <= segmentLengthList.length; i++) {
            let ty = (len - curvePositionLenAdjusted) / stampRealHeightHalf;
            ty = ty / 2 + 0.5;
            tyList.push(ty);
            len += segmentLengthList[i];
        }
        // console.log('tyList', tyList);

        let imgDataReader = new ImageDataReader(stamp);

        let ii = 0;
        let normal = new THREE.Vector3();
        let point = new THREE.Vector2();
        let lineNormal = new THREE.Vector2();
        let p = new THREE.Vector3();
        let arcLen2 = circlePosition; // 中心点的相对位置
        this.minPointZ = Infinity;
        let depthData = [];
        for (let i = 0, inRangeY = false, tx = 0, ty = 0; i < points.length; i++) {
            point = points[i];
            let s = point.x;
            let z = point.y;
            this.minPointZ = Math.min(this.minPointZ, z);
            if (i < topPointIndex || i > bottomPointIndex) {
                inRangeY = false;
            } else {
                ty = tyList[i - topPointIndex];
                inRangeY = ty >= 0 && ty <= 1.0;
                if (inRangeY) {
                    let a = points[i - 1];
                    let b = points[i];
                    let c = points[i + 1];
                    let ab = new THREE.Vector2().subVectors(b, a).normalize();
                    let bc = new THREE.Vector2().subVectors(c, b).normalize();
                    let n_ab = new THREE.Vector2(-ab.y, ab.x);
                    let n_bc = new THREE.Vector2(-bc.y, bc.x);
                    lineNormal.addVectors(n_ab, n_bc).normalize().negate();
                }
            }
            for (let j = 0; j < NECK_DIVISION; j++) {
                let arcLen1 = j / NECK_DIVISION; // 当前位置的相对位置
                let rad = arcLen1 * 2 * Math.PI;
                let cos = Math.cos(rad);
                let sin = Math.sin(rad);
                let x = s * cos;
                let y = s * sin;

                if (inRangeY) {
                    if (repeatTimes) { tx = ((arcLen1 - arcLen2) / stampWidthHalf / 2 + 0.5) % 1.0; if (tx < 0) { tx += 1.0; } }
                    else if (keepSize) { tx = (arcLen1 - arcLen2) * 2 * Math.PI * s / stampRealWidthHalf / 2 + 0.5; }
                    else { tx = (arcLen1 - arcLen2) / stampWidthHalf / 2 + 0.5; }

                    if (tx >= 0 && tx <= 1.0) {
                        let sx = tx * (stamp.width - 1);
                        let sy = ty * (stamp.height - 1);
                        let depth = imgDataReader.getGrayValue(sx, sy);
                        normal.set(-lineNormal.x * cos, -lineNormal.x * sin, -lineNormal.y);
                        let offset = depth * reliefThickness;
                        p.set(x, y, z).addScaledVector(normal, offset);
                        depthData.push(offset);
                        vertices[ii] = p.x;
                        vertices[ii + 1] = p.y;
                        vertices[ii + 2] = p.z;
                        ii += 3;
                        continue;
                    } else {
                        depthData.push(0);
                    }
                }

                vertices[ii] = x;
                vertices[ii + 1] = y;
                vertices[ii + 2] = z;
                ii += 3;
            }
        }

        if (reuseGeometry) {
            this.mesh.geometry.attributes.position.needsUpdate = true;
            this.mesh.geometry.computeVertexNormals();
        } else {
            let geom = new THREE.BufferGeometry();
            geom.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
            geom.setIndex(new THREE.BufferAttribute(indices, 1));
            geom.computeVertexNormals();
            this.mesh.geometry.dispose();
            this.mesh.geometry = geom;
        }
        // console.timeEnd("makeCircleWithRelief3");
        this.mesh.material.visible = true;
        this.meshForDisplay.material.visible = false;

        this.reliefData = {
            NECK_DIVISION, repeatTimes, keepSize, circlePosition,
            stampWidthHalf, stampRealWidthHalf, reliefThickness,
            points, topPointIndex, bottomPointIndex, tyList, depthData
        };
    }

    makeGridGeometry(pointsLen, NECK_DIVISION) {
        let vertices = new Float32Array(pointsLen * NECK_DIVISION * 3);
        let indices = new Uint32Array(pointsLen * NECK_DIVISION * 2 * 3);
        for (let i = 0, ii = 0, lastCircleBegin = pointsLen * NECK_DIVISION - NECK_DIVISION; i < pointsLen; i++) {
            let thisCircleBegin = i * NECK_DIVISION;
            for (let j = 0; j < NECK_DIVISION; j++) {
                let k = nextIndex(j, NECK_DIVISION);
                let i1 = lastCircleBegin + j;
                let i2 = thisCircleBegin + j;
                let i3 = lastCircleBegin + k;
                let i4 = thisCircleBegin + k;
                indices[ii] = i1; indices[ii + 1] = i3; indices[ii + 2] = i4;
                indices[ii + 3] = i1; indices[ii + 4] = i4; indices[ii + 5] = i2;
                ii += 6;
            }
            lastCircleBegin = thisCircleBegin;
        }
        return { vertices, indices };
    }

    // 圆形口
    makeCircle(curve) {
        // console.time("makeCircle");
        let points = this.getSolidPoints(curve);
        let NECK_DIVISION = this.CIRCLE_DIVISION;
        let reuseGeometry = false;
        let vertices;
        let indices;
        const TYPE = "circle";
        if (this.lastType === TYPE && points.length === this.lastCurvePointsLength && NECK_DIVISION === this.lastCirclePointsLength) {
            reuseGeometry = true;
            vertices = this.mesh.geometry.attributes.position.array;
        } else {
            this.lastType === TYPE;
            this.lastCurvePointsLength = points.length;
            this.lastCirclePointsLength = NECK_DIVISION;
            let gridGeomData = this.makeGridGeometry(points.length, NECK_DIVISION);
            vertices = gridGeomData.vertices;
            indices = gridGeomData.indices;
        }

        this.minPointZ = Infinity;
        for (let i = 0, ii = 0; i < points.length; i++) {
            let point = points[i];
            let s = -point.x;
            let z = -point.y;
            this.minPointZ = Math.min(this.minPointZ, z);
            for (let j = 0; j < NECK_DIVISION; j++) {
                let rad = Math.PI * 2 * j / NECK_DIVISION;
                let x = s * Math.cos(rad);
                let y = s * Math.sin(rad);
                vertices[ii] = x;
                vertices[ii + 1] = y;
                vertices[ii + 2] = z;
                ii += 3;
            }
        }

        if (reuseGeometry) {
            this.mesh.geometry.attributes.position.needsUpdate = true;
            this.mesh.geometry.computeVertexNormals();
        } else {
            let geom = new THREE.BufferGeometry();
            geom.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
            geom.setIndex(new THREE.BufferAttribute(indices, 1));
            geom.computeVertexNormals();
            this.mesh.geometry.dispose();
            this.mesh.geometry = geom;
        }
        // console.timeEnd("makeCircle");
    }

    getRotationFunction() {
        let rotation = this.rotation;
        // 输入值，t，范围[0, 1]
        switch (this.rotationType) {
            case "line": return t => t * rotation;
            case ">": return t => t >= 0 && t <= 0.5 ? t * rotation * 2 / 2 : (2 - t * 2) * rotation / 2;
            case "sin": return t => Math.sin(t * Math.PI) * rotation / 2;
            case "sin2": return t => Math.sin(t * Math.PI * 2) * rotation / 3;
        }
    }

    // 正多边形口
    makeRegularPolygon(curve, controlPoints) {
        // console.time("updateMesh");
        let points = this.getSolidPoints(curve);
        points.forEach(p => {
            p.x = -p.x;
            p.y = -p.y;
        });
        let NECK_DIVISION = this.edgeIsLine ? this.repeatTimes : (this.repeatTimes * this.EACH_DIVISION);

        let reuseGeometry = false;
        let vertices;
        let indices;
        const TYPE = "regularPolygon";
        if (this.lastType === TYPE && points.length === this.lastCurvePointsLength && NECK_DIVISION === this.lastCirclePointsLength) {
            reuseGeometry = true;
            vertices = this.mesh.geometry.attributes.position.array;
        } else {
            this.lastType === TYPE;
            this.lastCurvePointsLength = points.length;
            this.lastCirclePointsLength = NECK_DIVISION;
            let gridGeomData = this.makeGridGeometry(points.length, NECK_DIVISION);
            vertices = gridGeomData.vertices;
            indices = gridGeomData.indices;
        }

        let points3 = [];
        let rad_each_half = Math.PI / this.repeatTimes;
        let center = new THREE.Vector2();
        if (this.edgeIsLine) {
            for (let i = 0; i < this.repeatTimes; i++) {
                let rot = (2 * i + 1) / this.repeatTimes * Math.PI;
                points3.push(new THREE.Vector2(Math.cos(rot), Math.sin(rot)));
            }
        } else {
            let curve2 = new THREE.SplineCurve(controlPoints);
            let points2 = curve2.getPoints(this.EACH_DIVISION);
            let targetX = Math.cos(rad_each_half);
            let targetY = Math.sin(rad_each_half);
            let scale = targetY / points2[0].y;
            for (let i = 0; i < points2.length; i++) {
                points2[i].multiplyScalar(scale);
            }
            let translateX = targetX - points2[0].x;
            for (let i = 0; i < points2.length; i++) {
                points2[i].x += translateX;
            }

            let rad_each = rad_each_half * 2;
            for (let i = 0; i < this.repeatTimes; i++) {
                let toRotate = -i * rad_each;
                for (let j = 0; j < points2.length - 1; j++) {
                    let p = points2[j].clone();
                    p.rotateAround(center, toRotate);
                    points3.push(p);
                }
            }
            points3.reverse();
        }

        let zMin = Infinity, zMax = -Infinity;
        for (let p of points) {
            zMin = Math.min(p.y, zMin);
            zMax = Math.max(p.y, zMax);
        }

        let zRange = zMax - zMin;
        this.minPointZ = zMin;

        let getRotation = this.getRotationFunction();

        for (let i = 0, ii = 0; i < points.length; i++) {
            let s = points[i].x;
            let z = points[i].y;
            let t = (z - zMin) / zRange;
            let rot = getRotation(t);
            for (let j = 0; j < points3.length; j++) {
                let p2 = points3[j].clone();
                p2.rotateAround(center, rot);
                let x = s * p2.x;
                let y = s * p2.y;
                vertices[ii] = x;
                vertices[ii + 1] = y;
                vertices[ii + 2] = z;
                ii += 3;
            }
        }

        if (reuseGeometry) {
            this.mesh.geometry.attributes.position.needsUpdate = true;
            this.mesh.geometry.computeVertexNormals();
        } else {
            let geom = new THREE.BufferGeometry();
            geom.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
            geom.setIndex(new THREE.BufferAttribute(indices, 1));
            geom.computeVertexNormals();
            this.mesh.geometry.dispose();
            this.mesh.geometry = geom;
        }
        // console.timeEnd("updateMesh");
    }

    // 任意多边形
    makePolygon(curve, controlPoints) {
        // console.time("makePolygon");
        let points = this.getSolidPoints(curve);
        points.forEach(p => {
            p.x = -p.x * 0.018;
            p.y = -p.y;
        });
        let NECK_DIVISION = this.edgeIsLine ? controlPoints.length : this.CIRCLE_DIVISION;
        let reuseGeometry = false, vertices, indices;
        const TYPE = "polygon";
        if (this.lastType === TYPE && points.length === this.lastCurvePointsLength && NECK_DIVISION === this.lastCirclePointsLength) {
            reuseGeometry = true;
            vertices = this.mesh.geometry.attributes.position.array;
        } else {
            this.lastType === TYPE;
            this.lastCurvePointsLength = points.length;
            this.lastCirclePointsLength = NECK_DIVISION;
            let gridGeomData = this.makeGridGeometry(points.length, NECK_DIVISION);
            vertices = gridGeomData.vertices;
            indices = gridGeomData.indices;
        }

        let zMin = Infinity, zMax = -Infinity;
        for (let p of points) {
            zMin = Math.min(p.y, zMin);
            zMax = Math.max(p.y, zMax);
        }

        let zRange = zMax - zMin;
        this.minPointZ = zMin;

        let points2 = this.edgeIsLine ? controlPoints : new ClosedSplineCurve(controlPoints).getPoints(NECK_DIVISION);
        points2.forEach(p => { p.x = -p.x; });
        let center = new THREE.Vector2();

        let getRotation = this.getRotationFunction();

        for (let i = 0, ii = 0; i < points.length; i++) {
            let point = points[i];
            let s = point.x;
            let z = point.y;
            let t2 = (z - zMin) / zRange;
            let rot = getRotation(t2);
            for (let j = 0; j < points2.length; j++) {
                let p2 = points2[j].clone();
                p2.rotateAround(center, rot);
                let x = s * p2.x;
                let y = s * p2.y;
                vertices[ii] = x;
                vertices[ii + 1] = y;
                vertices[ii + 2] = z;
                ii += 3;
            }
        }

        if (reuseGeometry) {
            this.mesh.geometry.attributes.position.needsUpdate = true;
            this.mesh.geometry.computeVertexNormals();
        } else {
            let geom = new THREE.BufferGeometry();
            geom.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
            geom.setIndex(new THREE.BufferAttribute(indices, 1));
            geom.computeVertexNormals();
            this.mesh.geometry.dispose();
            this.mesh.geometry = geom;
        }
        // console.log('reuseGoemetry', reuseGeometry);
        // console.timeEnd("makePolygon");
    }
}




// WEBPACK FOOTER //
// ./src/views/reliefVase/scripts/VaseMaker.js